// Client-side error reporting service for production monitoring
import { authService } from './authService';
import { SupabaseClient } from '@supabase/supabase-js';
import { supabaseClient } from './supabaseClient';

interface ClientError {
  message: string;
  stack?: string;
  type: 'error' | 'unhandledRejection' | 'react' | 'api';
  url: string;
  userAgent: string;
  timestamp: string;
  context?: any;
  userId?: string;
  sessionId?: string;
}

class ClientErrorService {
  private supabase: SupabaseClient;
  private errors: ClientError[] = [];
  private maxErrors = 50;
  private initialized = false;
  private sessionId: string;

  constructor() {
    this.supabase = supabaseClient;
    this.sessionId = this.generateSessionId();
  }
  
  getSupabase() {
    return this.supabase;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Global error handler
    window.addEventListener('error', (event) => {
      this.captureError({
        message: event.error?.message || event.message,
        stack: event.error?.stack,
        type: 'error',
        context: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
    });

    // Promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.captureError({
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        type: 'unhandledRejection',
        context: {
          promise: String(event.promise)
        }
      });
    });

    // Override console.error for React errors
    const originalError = console.error;
    console.error = (...args) => {
      const errorString = args.join(' ');
      if (errorString.includes('React')) {
        this.captureError({
          message: errorString,
          type: 'react',
          context: { args }
        });
      }
      originalError.apply(console, args);
    };

    // Monitor API calls
    this.monitorFetch();

    console.log('[ClientErrorService] Initialized');
  }

  private monitorFetch() {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const start = performance.now();
      const [url, options] = args;

      try {
        const response = await originalFetch(...args);
        const duration = performance.now() - start;

        // Log slow or failed requests
        if (!response.ok || duration > 3000) {
          this.captureError({
            message: `API ${response.ok ? 'slow' : 'failed'}: ${url}`,
            type: 'api',
            context: {
              url,
              method: options?.method || 'GET',
              status: response.status,
              statusText: response.statusText,
              duration
            }
          });
        }

        return response;
      } catch (error) {
        this.captureError({
          message: `API error: ${url}`,
          stack: error.stack,
          type: 'api',
          context: {
            url,
            method: options?.method || 'GET',
            error: error.message
          }
        });
        throw error;
      }
    };
  }

  captureError(error: Partial<ClientError>) {
    const fullError: ClientError = {
      message: error.message || 'Unknown error',
      stack: error.stack,
      type: error.type || 'error',
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      context: error.context,
      userId: authService.getCurrentUserId() || undefined,
      sessionId: this.sessionId
    };

    // Add to local buffer
    this.errors.push(fullError);
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    // Log to console for immediate visibility
    console.error('[ClientError]', fullError);

    // Send to Supabase
    this.sendToSupabase(fullError);
  }

  private async sendToSupabase(error: ClientError) {
    try {
      // Check if table exists first
      const { error: tableError } = await this.supabase
        .from('client_errors')
        .select('id')
        .limit(1);

      if (!tableError) {
        // Table exists, insert error
        await this.supabase
          .from('client_errors')
          .insert({
            error_data: error,
            user_id: error.userId || null,
            created_at: error.timestamp
          });
      }
    } catch (e) {
      // Silently fail - don't create more errors
      console.warn('[ClientErrorService] Failed to send error to Supabase:', e);
    }
  }

  // For React Error Boundaries
  logErrorBoundary(error: Error, errorInfo: any) {
    this.captureError({
      message: error.message,
      stack: error.stack,
      type: 'react',
      context: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true
      }
    });
  }

  getErrors(): ClientError[] {
    return [...this.errors];
  }

  clearErrors() {
    this.errors = [];
  }

  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // For DXT monitoring integration
  getMonitoringData() {
    return {
      errors: this.errors,
      sessionId: this.sessionId,
      errorCount: this.errors.length,
      apiErrors: this.errors.filter(e => e.type === 'api').length,
      reactErrors: this.errors.filter(e => e.type === 'react').length
    };
  }
}

export const clientErrorService = new ClientErrorService();